import React, { useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import { useNavigate, Link } from "react-router-dom";
import api from "../config/api";
import loading from "../lottie/loading.json";
import {
  trackBiscuitScanStart,
  trackBiscuitScanSuccess,
  trackBiscuitScanFailure,
  trackKuralUnlocked,
  trackCashbackClaimTap,
  trackGetHelpTap,
  trackHomeTap,
} from '../utils/ga4Events';
import MenuPopup from '../components/MenuPopup';
import { getPostBiscuitScanTarget } from "../utils/journeyFlow";
import { redirectToRoute } from "../utils/routeRedirect";

// Helper: dataURL → File object
function dataURLtoFile(dataUrl, filename) {
  const [header, data] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(data);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) arr[i] = binary.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

function BiscuitScan() {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  const [showMenu, setShowMenu] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [popupType, setPopupType] = useState("help");
  const [popupData, setPopupData] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);

  const scanStartTimeRef = useRef(null);
  const biscuitScanFailCountRef = useRef(0);

  const language = localStorage.getItem("appLanguage") || "en";

  // ─── Camera ───────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setCameraActive(true);
    } catch {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
        setCameraActive(true);
      } catch (err) {
        console.error("Camera access denied:", err);
        setCameraError(true);
        setPopupData(null);
        setPopupType("camera_not_found");
        setShowPopup(true);
      }
    }
  }, []);

  // ─── Capture frame from video ──────────────────────────────
  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return null;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", 0.8);
  };

  // ─── Main capture + API call ───────────────────────────────
  const handleCapture = async () => {
    if (!cameraActive) {
      await startCamera();
      return;
    }

    const imgDataUrl = captureFrame();
    if (!imgDataUrl) return;

    setCapturedImage(imgDataUrl);
    setPopupData(null);
    setPopupType("loading");
    setShowPopup(true);
    scanStartTimeRef.current = Date.now();
    trackBiscuitScanStart();

    try {
      const mobile = localStorage.getItem("userMobile") || "";
      const imageFile = dataURLtoFile(imgDataUrl, "biscuit.jpg");
      const formData = new FormData();
      formData.append("image", imageFile);
      formData.append("mobile", mobile);

      const response = await api.post("/detect-biscuit-text", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });

      const { success, reason, status, kural_matches, popup } = response.data;

      // ── Backend returned failure (2xx body) ──────────────
      if (!success) {
        if (status === "TRY_AGAIN") {
          biscuitScanFailCountRef.current += 1;
          trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'unreadable', attempt_count: biscuitScanFailCountRef.current });
          setPopupData({ reason: reason || t("scan.textUnreadable") });
          setPopupType("try_again");
          return;
        }
        biscuitScanFailCountRef.current += 1;
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: reason || 'no_biscuit', attempt_count: biscuitScanFailCountRef.current });
        setPopupData({ reason: reason || t("scan.noBiscuit") });
        setPopupType("error");
        return;
      }

      // ── No matches array at all ───────────────────────────
      if (!kural_matches || kural_matches.length === 0) {
        biscuitScanFailCountRef.current += 1;
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'no_match', attempt_count: biscuitScanFailCountRef.current });
        setPopupData({ reason: t("scan.noBiscuit") });
        setPopupType("error");
        return;
      }

      // ── Has matches but none are confident ────────────────
      const confidentMatches = kural_matches.filter((m) => m.confident);
      if (confidentMatches.length === 0) {
        biscuitScanFailCountRef.current += 1;
        trackBiscuitScanFailure({ kural_id: null, word_position: null, failure_reason: 'low_confidence', attempt_count: biscuitScanFailCountRef.current });
        setPopupData({ reason: t("scan.textUnreadable") });
        setPopupType("try_again");
        return;
      }

      // ── Success ───────────────────────────────────────────
      const scanDuration = scanStartTimeRef.current ? Date.now() - scanStartTimeRef.current : null;
      const topKural = confidentMatches[0];
      trackBiscuitScanSuccess({
        kural_id: topKural?.kural_id || topKural?.kuralNumber || '',
        scan_duration_ms: scanDuration,
        cashback_amount: topKural?.cashback || 0,
      });
      trackKuralUnlocked({
        kural_id: topKural?.kural_id || topKural?.kuralNumber || '',
        kural_chapter: topKural?.chapter || '',
        cashback_amount: topKural?.cashback || 0,
      });

      const popupKind =
        popup === "success-without-cashback"
          ? "success-without-cashback"
          : "success";

      setPopupData({ kural_matches });
      setPopupType(popupKind);

      if (response.data.showCompletionPopup && response.data.completedKuralNumber) {
        localStorage.setItem(
          "pendingKuralComplete",
          String(response.data.completedKuralNumber)
        );
      }

      // ── Auto-call cashback API silently in background ─────
      if (popupKind === "success") {
        try {
          await api.post("user/enable-cashback", {}, { withCredentials: true });
        } catch (cashbackErr) {
          console.error("Auto cashback API error:", cashbackErr);
          // silently ignore — flow continues normally
        }
      }

    } catch (err) {
      console.error("API error:", err);
      const resData = err?.response?.data;
      const serverStatus = resData?.status;
      const httpStatus = err?.response?.status;
      const serverReason =
        resData?.reason || resData?.error || err?.message;

      if (
        httpStatus === 403 &&
        serverStatus === "INSUFFICIENT_CREDITS"
      ) {
        setPopupData({
          reason:
            serverReason ||
            "You are out of scans! Scan a new pack to continue scanning.",
        });
        setPopupType("oops_simple");
        return;
      }

      if (httpStatus === 409 && serverStatus === "ALREADY_SCANNED") {
        setPopupData({
          reason:
            serverReason ||
            "This biscuit has already been scanned!",
        });
        setPopupType("oops_simple");
        return;
      }

      if (serverReason === "Text is unreadable") {
        setPopupData({ reason: t("scan.textUnreadable") });
        setPopupType("try_again");
        return;
      }

      setPopupData({ reason: serverReason || t("scan.genericError") });
      setPopupType("error");
    }
  };

  // ─── Close popup ──────────────────────────────────────────
  const closePopup = () => {
    setShowPopup(false);
    setPopupData(null);
    setCapturedImage(null);
  };

  // ─── Retry capture ────────────────────────────────────────
  const retryCapture = () => {
    closePopup();
    setTimeout(() => {
      if (!cameraActive) startCamera();
    }, 200);
  };

  useEffect(() => {
    startCamera();
  }, []);

  useEffect(() => {
    if (showPopup && popupType === 'help') {
      trackHowToPlayClick();
    }
  }, [showPopup, popupType]);

  const trackHowToPlayClick = () => {
    api.post('/interaction/track', { action: 'HOW_TO_PLAY_CLICKED' });
  };

  // ─── Popup content based on type ──────────────────────────
  const renderPopupContent = () => {
    switch (popupType) {

      // ── Camera Not Found ───────────────────────────────────
      case "camera_not_found":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img
                    src="assets/images/language-popup-bg.webp"
                    className="img-fit"
                    alt="Language Background"
                  />
                  <div className="bis-error-text">
                    <img
                      src={
                        language === "ta"
                          ? "assets/images/oops-tamil-icon.webp"
                          : "assets/images/oops-icon.webp"
                      }
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p
                      style={{
                        fontSize: language === "ta" ? "10px" : "13px",
                        padding: language === "ta" ? "0 14px" : "0",
                      }}
                    >
                      {t("scan.cameraNotFound")}{" "}
                      <span className="fw-700">{t("scan.cameraAccess")}</span>
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                    onClick={() => { window.location.href = "/home"; }}
                  >
                    <span>{t("common.goBack")}</span>
                  </button>
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                    onClick={() => {
                      closePopup();
                      setTimeout(() => startCamera(), 300);
                    }}
                  >
                    <span>{t("common.retry")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Loading ────────────────────────────────────────────
      case "loading":
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Lottie animationData={loading} loop={true} />
          </div>
        );

      // ── Success (with cashback) ────────────────────────────
      case "success":
        return (
          <div className="scan-popup">
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />

            <div className="scan-success-level-1-box">
              <div className="scan-success-level-1-box-scroll">
                <div className="scan-success-level-1-box-inner">
                  <div className="star-rotate">
                    <img src="assets/images/cashback-star-element.webp" className="orange-star-bg img-fit" />
                  </div>

                  <div className="scan-success-level-1-box-text scan-instruction-box">
                    <div className="cashback-wrapper">
                      <img src={language === "ta" ? "assets/images/voucher-text-tamil.webp" : "assets/images/voucher-text.webp"} className="cashback-icon voucher-icon" />
                    </div>
                    <p>
                      {language === "en" && <b>{t("claimCashback.congratulations")}<br /></b>}
                      {t("claimCashback.step1Text")}
                    </p>
                  </div>
                </div>

                <div className="scan-success-level-1-box-footer">
                  <button
                    className="btn-next claim-reward-btn-level-1"
                    type="button"
                    onClick={() => {
                      trackCashbackClaimTap({ reward_amount: popupData?.kural_matches?.[0]?.cashback ?? null });
                      setShowPopup(false);
                      setPopupData(null);
                      setPopupType("loading");
                      redirectToRoute(navigate, getPostBiscuitScanTarget());
                    }}
                  >
                    <span>{t("claimCashback.step1Button")}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      // ── Oops (single message + Go Back) ───────────────────
      case "oops_simple":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img
                    src="assets/images/language-popup-bg.webp"
                    className="img-fit"
                    alt="Language Background"
                  />
                  <div className="bis-error-text">
                    <img
                      src={
                        language === "ta"
                          ? "assets/images/oops-tamil-icon.webp"
                          : "assets/images/oops-icon.webp"
                      }
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p
                      style={{
                        fontSize: language === "ta" ? "10px" : "13px",
                        padding: language === "ta" ? "0 14px" : "0",
                        maxWidth: "220px",
                        margin: "0 auto",
                        textAlign: "center",
                        lineHeight: "1.35",
                      }}
                    >
                      {popupData?.reason || t("scan.genericError")}
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                    onClick={() => { window.location.href = "/home"; }}
                  >
                    <span>{t("popup.goBack")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Try Again ──────────────────────────────────────────
      case "try_again":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img
                    src="assets/images/language-popup-bg.webp"
                    className="img-fit"
                    alt="Language Background"
                  />
                  <div className="bis-error-text">
                    <img
                      src={
                        language === "ta"
                          ? "assets/images/oops-tamil-icon.webp"
                          : "assets/images/oops-icon.webp"
                      }
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p
                      style={{
                        fontSize: language === "ta" ? "10px" : "13px",
                        padding: language === "ta" ? "0 14px" : "0",
                      }}
                    >
                      {t("scan.scanFailed")}{" "}
                      <span className="fw-700">{t("scan.tryAgain")}</span>
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                    onClick={() => { window.location.href = "/home"; }}
                  >
                    <span>{t("common.goBack")}</span>
                  </button>
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                    onClick={closePopup}
                  >
                    <span>{t("common.scan")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Error ──────────────────────────────────────────────
      case "error":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="biscuit-error-box">
              <div className="error-bis-scroll">
                <div className="bis-error-scroll-inner">
                  <img
                    src="assets/images/language-popup-bg.webp"
                    className="img-fit"
                    alt="Language Background"
                  />
                  <div className="bis-error-text">
                    <img
                      src={
                        language === "ta"
                          ? "assets/images/oops-tamil-icon.webp"
                          : "assets/images/oops-icon.webp"
                      }
                      className="oops-icon"
                      alt="Oops Icon"
                    />
                    <p
                      style={{
                        fontSize: language === "ta" ? "10px" : "13px",
                        padding: language === "ta" ? "0 14px" : "0",
                      }}
                    >
                      {t("scan.scanFailed")}{" "}
                      <span className="fw-700">{t("scan.tryAgain")}</span>
                    </p>
                  </div>
                </div>
                <div className="bis-error-scroll-footer">
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    onClick={() => { window.location.href = "/home"; }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                  >
                    <span>{t("common.goBack")}</span>
                  </button>
                  <button
                    className="btn-next error-bis-btn"
                    type="button"
                    onClick={closePopup}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: "8px 0px",
                    }}
                  >
                    <span>{t("common.scan")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Success Without Cashback ───────────────────────────
      case "success-without-cashback":
        return (
          <>
            <img src="assets/images/rays.webp" className="popup-rays" alt="Rays" />
            <div className="scan-success-level-1-box">
              <div className="scan-success-level-1-box-scroll">
                <div className="scan-success-level-1-box-inner">
                  <div className="star-rotate">
                    <img
                      src="assets/images/Star-2.webp"
                      className="orange-star-bg img-fit"
                      alt="Star"
                    />
                  </div>
                  <div className="scan-success-level-1-box-text">
                    <div className="cashback-wrapper">
                      <img
                        src={
                          language === "ta"
                            ? "assets/images/machineCodeScan-tamil.webp"
                            : "assets/images/your-scan-was-SUCCESSFul.webp"
                        }
                        className="cashback-icon"
                        alt="Scan Success"
                      />
                    </div>
                  </div>
                </div>
                <div className="scan-success-level-1-box-footer">
                  <button
                    className="btn-next claim-reward-btn-level-1"
                    type="button"
                    onClick={() => { window.location.href = "/home"; }}
                  >
                    <span>{t("common.continue")}</span>
                  </button>
                </div>
              </div>
            </div>
          </>
        );

      // ── Help (default) ─────────────────────────────────────
      case "help":
      default:
        return (
          <div className="popup-card">
            <img
              src="assets/images/star-element.webp"
              className="popup-star"
              alt="Sparkle Element"
            />
            <div className="popup-inner">
              <p className="popup-text" style={{
                paddingLeft: language === "en" ? "20px" : "0",
                paddingRight: language === "en" ? "20px" : "0",
              }}>{t("scan.scanYourKural")}</p>
              <img
                src="assets/images/referance.webp"
                style={{ height: '180px' }}
                className="popup-image"
                alt="Scan Help"
              />
            </div>
            <button
              className="btn-next popup-btn"
              type="button"
              onClick={closePopup}
            >
              <span>{t("common.continue")}</span>
            </button>
          </div>
        );
    }
  };

  // ─── Render ────────────────────────────────────────────────
  return (
    <>
      <meta charSet="UTF-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1"
      />
      <link
        rel="shortcut icon"
        href="assets/images/favicon.ico"
        type="image/x-icon"
      />
      <link rel="stylesheet" href="assets/css/styles.css?ver=2.0" />
      <title>Biscuit Scan Level 1 | Britannia</title>

      <style>{`
        .popup-loader {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 12px 0;
        }
        .loader-spinner {
          width: 48px;
          height: 48px;
          border: 5px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .popup-subtext {
          font-size: 13px;
          opacity: 0.8;
          margin-top: 4px;
          text-align: center;
        }
        .popup-kural-words {
          font-size: 14px;
          font-weight: 600;
          margin-top: 6px;
          text-align: center;
          letter-spacing: 0.5px;
        }
        .popup-success-icon {
          font-size: 36px;
          color: #4caf50;
          text-align: center;
          margin-bottom: 4px;
        }
        .popup-warn-icon {
          font-size: 36px;
          color: #ff9800;
          text-align: center;
          margin-bottom: 4px;
        }
        .popup-error-icon {
          font-size: 36px;
          color: #f44336;
          text-align: center;
          margin-bottom: 4px;
        }
      `}</style>

      <div className="page-wrapper biscuit-scan-level1-page">
        <div className="main-wrapper yellow-bg">

          {/* ── DESKTOP ───────────────────────────────────────── */}
          <div className="d-frame-outer desktop-only">
            <img
              src="assets/images/desktop-star.webp"
              className="d-frame-star-bg"
              alt="Star Background"
            />
            <span className="d-frame-corner tl" />
            <span className="d-frame-corner tr" />
            <span className="d-frame-corner bl" />
            <span className="d-frame-corner br" />
            <div className="d-frame-border-outer">
              <div className="d-frame-border">
                <div className="d-frame-border-inner">
                  <div className="d-frame-canvas">
                    <div className="d-frame-content">
                      <div className="desktop-center">
                        <div className="desktop-content-box">
                          <h3>{t("common.desktopInterest")}</h3>
                          <img
                            src="assets/images/desktop-logo-milk-bikis.webp"
                            className="desktop-logo"
                            alt="Milk Bikis Logo"
                          />
                          <div className="desktop-qr-box">
                            <span className="qr-corner qr-corner-tl" />
                            <span className="qr-corner qr-corner-tr" />
                            <span className="qr-corner qr-corner-bl" />
                            <span className="qr-corner qr-corner-br" />
                            <div className="qr-left">
                              <p className="qr-text">{t("common.desktopQr")}</p>
                            </div>
                            <div className="qr-right">
                              <img
                                src="assets/images/desktop-qr.svg"
                                alt="QR Code"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── MOBILE ────────────────────────────────────────── */}
          <div className="frame-yellow-bg mobile-only">
            <div className="frame-green-corners">
              <div className="frame-canvas br-16">
                <div className="frame-content">

                  <div className="container-full">
                    <div className="top-logo">
                      <div className="camera-sc-logo-wrap">
                        <img src="assets/images/animate-britania-logo.webp" alt="Britannia" className="camera-sc-logo camera-sc-logo-1" />
                        <img src="assets/images/animate-milk-bikkis-logo.webp" alt="Milk Bikis" className="camera-sc-logo camera-sc-logo-2" />
                      </div>
                      <img
                        src="assets/images/sign-up-head-text.webp"
                        className="top-logo-text"
                        alt="Top Logo"
                        onClick={() => {
                          trackHomeTap({ from_screen: 'biscuit_scan', kural_in_progress: false });
                          window.location.href = "/home";
                        }}
                      />
                      <div className="menu-bars" onClick={() => navigate("/menu")}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                    <div className="camera-container">
                      <p className="scan-text">
                        {t("scan.level1")} <br />
                        <span className="fw-400" style={{ fontSize: "16px" }}>
                          {t("scan.scanBiscuit")}{" "}
                          <img
                            src="assets/images/question-icon.webp"
                            className="question-icon"
                            alt="?"
                            onClick={() => {
                              setPopupType('help');
                              setShowPopup(true);
                            }}
                          />
                        </span>
                      </p>

                      {/* Scanner Box */}
                      <div
                        className="scanner-box"
                        style={{ position: "relative" }}
                      >
                        <video
                          ref={videoRef}
                          autoPlay
                          playsInline
                          muted
                          style={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <canvas ref={canvasRef} style={{ display: "none" }} />
                        <img src="assets/images/corner-tl.webp" className="corner tl" alt="" />
                        <img src="assets/images/corner-tr.webp" className="corner tr" alt="" />
                        <img src="assets/images/corner-bl.webp" className="corner bl" alt="" />
                        <img src="assets/images/corner-br.webp" className="corner br" alt="" />
                      </div>

                      {/* Capture Button */}
                      <button
                        className="camera-btn"
                        type="button"
                        onClick={handleCapture}
                        disabled={showPopup && popupType === "loading"}
                      >
                        <img
                          src="assets/images/camera-icon.webp"
                          alt="Capture"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="frame-footer get-help-foot">
              <div className="frame-footer-pill">
                <h3>
                  <a
                    href="#"
                    style={{
                      fontSize: language === "ta" ? "12px" : "15px",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      trackGetHelpTap({ current_screen: 'biscuit_scan' });
                      navigate("/contact-us");
                    }}
                  >
                    {t("common.getHelp")}
                  </a>
                </h3>
              </div>
              <p className="frame-footer-terms">
                <Link to="/tnc">{t("common.terms")}</Link> {t("common.and")} <Link to="/privacy-policy">{t("common.privacy")}</Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── POPUP ─────────────────────────────────────────────── */}
      {showPopup && (
        <div className="scan-popup">
          {renderPopupContent()}
        </div>
      )}
    </>
  );
}

export default BiscuitScan;
